import axiosInstance from './AxiosInstance';

const PER_MODULE_LIMIT = 5;

function getCorporateId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.corporateId || user?.id || null;
  } catch {
    return null;
  }
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function pickList(payload, keys) {
  if (!payload) return [];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
    if (Array.isArray(payload?.data?.[key])) return payload.data[key];
  }
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

/**
 * Search across main modules for the dashboard global search panel.
 * @param {string} query
 * @returns {Promise<{ groups: Array<{ key: string, title: string, viewAllTo: string, items: Array<{ id: string|number, title: string, subtitle?: string, to: string }> }> }>}
 */
export async function searchAcrossModules(query) {
  const q = String(query || '').trim();
  if (q.length < 2) {
    return { groups: [] };
  }

  const corporateId = getCorporateId();
  const encoded = encodeURIComponent(q);

  const [tasksRes, projectsRes, forumsRes, docsRes, vaultRes] = await Promise.allSettled([
    axiosInstance.get('/task/list', { params: { search: q, limit: PER_MODULE_LIMIT, page: 1 } }),
    axiosInstance.get('/project/list', { params: { search: q, limit: PER_MODULE_LIMIT, page: 1 } }),
    axiosInstance.get('/forum/forums', { params: { search: q, limit: PER_MODULE_LIMIT, page: 1 } }),
    corporateId
      ? axiosInstance.get('/documents/list', {
          params: { search: q, corporate_id: corporateId, limit: PER_MODULE_LIMIT, page: 1 },
        })
      : Promise.reject(new Error('missing corporate_id')),
    axiosInstance.get('/credential-vault', { params: { search: q, limit: PER_MODULE_LIMIT, page: 1 } }),
  ]);

  const groups = [];

  if (tasksRes.status === 'fulfilled') {
    const tasks = pickList(tasksRes.value?.data, ['tasks', 'data']).slice(0, PER_MODULE_LIMIT);
    if (tasks.length) {
      groups.push({
        key: 'tasks',
        title: 'Tasks',
        viewAllTo: `/task/tasklist?search=${encoded}`,
        items: tasks.map((t) => ({
          id: t.id || t.taskid,
          title: t.title || t.task_name || 'Untitled task',
          subtitle: t.status_label || t.priority || '',
          to: `/task/taskdetails/${t.id || t.taskid}`,
        })),
      });
    }
  }

  if (projectsRes.status === 'fulfilled') {
    const projects = pickList(projectsRes.value?.data, ['projects', 'data']).slice(0, PER_MODULE_LIMIT);
    if (projects.length) {
      groups.push({
        key: 'projects',
        title: 'Projects',
        viewAllTo: `/task/projectlist?search=${encoded}`,
        items: projects.map((p) => ({
          id: p.id,
          title: p.title || p.name || 'Untitled project',
          subtitle: p.status != null ? `Status ${p.status}` : '',
          to: `/task/projectdetails/${p.id}`,
        })),
      });
    }
  }

  if (forumsRes.status === 'fulfilled') {
    const forums = pickList(forumsRes.value?.data, ['items', 'forums', 'data']).slice(0, PER_MODULE_LIMIT);
    if (forums.length) {
      groups.push({
        key: 'forum',
        title: 'Forum',
        viewAllTo: `/forum/list?search=${encoded}`,
        items: forums.map((f) => ({
          id: f.id,
          title: f.title || 'Untitled discussion',
          subtitle: f.status || '',
          to: `/forum/detail/${f.id}`,
        })),
      });
    }
  }

  if (docsRes.status === 'fulfilled') {
    const docs = pickList(docsRes.value?.data?.data || docsRes.value?.data, ['documents', 'data']).slice(
      0,
      PER_MODULE_LIMIT
    );
    if (docs.length) {
      groups.push({
        key: 'documents',
        title: 'Documents',
        viewAllTo: `/documents/mydocuments?search=${encoded}`,
        items: docs.map((d) => ({
          id: d.id,
          title: d.title || d.document_name || d.name || 'Untitled document',
          subtitle: d.expiry_date ? `Expires ${d.expiry_date}` : '',
          to: `/documents/details/${d.id}`,
        })),
      });
    }
  }

  if (vaultRes.status === 'fulfilled') {
    const credentials = pickList(vaultRes.value?.data, ['credentials', 'items', 'data']).slice(
      0,
      PER_MODULE_LIMIT
    );
    if (credentials.length) {
      groups.push({
        key: 'vault',
        title: 'Vault',
        viewAllTo: `/credential-vault/credentialVaultList?search=${encoded}`,
        items: credentials.map((c) => ({
          id: c.id,
          title: c.title || c.name || c.username || 'Credential',
          subtitle: c.category || c.scope || '',
          to: `/credential-vault/credentialDetail/${c.id}`,
        })),
      });
    }
  }

  return { groups: asArray(groups) };
}
